<template>
  <page v-loading="isLoading">
    <page-header-new
      :breadcrumbs="breadcrumbs"
      :title="model ? model.title || model.name : ''"
      class="details-page-header"
    >
      <template #actions>
        <a
          class="btn-underlined"
          @click="backToList"
          data-qa="back-to-synced-rows-btn"
        >
          {{ $t('EmailRows_Action_BackToAll') }}
        </a>
        <template v-if="$can('EmailSavedRows::Delete')">
          <el-tooltip
            :disabled="usageCount === 0"
            effect="dark"
            :content="$t('EmailRows_DeleteDisabledTooltip')"
            placement="top"
          >
            <el-button
              type="primary"
              plain
              @click="confirmDelete"
              :disabled="usageCount > 0"
              data-qa="delete-synced-row-btn"
            >
              {{ $t('EmailRows_Action_DeleteSyncedRow') }}
            </el-button>
          </el-tooltip>
        </template>
        <el-button
          v-if="$can('EmailSavedRows::Update')"
          type="primary"
          @click="openEditor"
          data-qa="edit-synced-row-btn"
        >
          {{ $t('EmailRows_Action_EditSyncedRow') }}
        </el-button>
      </template>
    </page-header-new>

    <el-dialog
      :visible.sync="isEditing"
      :show-close="false"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      fullscreen
      custom-class="edit-synced-row-dialog"
      append-to-body
    >
      <edit-single-row-mode
        v-if="isEditing"
        :saved-row="model"
        :on-save="handleEditorSave"
        :on-cancel="handleEditorCancel"
      />
    </el-dialog>

    <page-content v-if="model" style="background: #fff">
      <div class="details-container">
        <div class="meta-info">
          <div class="meta-info__item">
            <span class="label">{{ $t('EmailRows_CreatedBy') }}</span>
            <div class="meta-info__value">
              <el-avatar
                :size="22"
                :src="model.creator.avatar"
                v-if="model.creator && model.creator.avatar"
              />
              <i v-else class="el-icon-user-solid meta-info__avatar-fallback" />
              <span class="username">
                {{ (model.creator && model.creator.name) || '-' }}
              </span>
            </div>
          </div>

          <div class="meta-info__item">
            <span class="label">{{ $t('EmailRows_UpdatedBy') }}</span>
            <div class="meta-info__value">
              <el-avatar
                :size="22"
                :src="model.updater.avatar"
                v-if="model.updater && model.updater.avatar"
              />
              <i v-else class="el-icon-user-solid meta-info__avatar-fallback" />
              <span class="username">
                {{ (model.updater && model.updater.name) || '-' }}
              </span>
              <span class="updated-at">
                {{ model.updated_at ? formatDate(model.updated_at) : '-' }}
              </span>
            </div>
          </div>

          <div class="meta-info__item">
            <span class="label">{{ $t('EmailRows_Version') }}</span>
            <div class="meta-info__value">
              <span class="username">{{ model.version || '-' }}</span>
            </div>
          </div>
        </div>

        <div class="usage-summary">
          <div class="usage-summary__total">
            <span class="usage-summary__label">
              {{ $t('EmailRows_UsedIn_Label') }}
            </span>
            <span class="usage-summary__total-count">
              {{
                $tc('EmailRows_Count_TotalInstances', usageCount, {
                  count: usageCount
                })
              }}
            </span>
            <el-tooltip
              effect="dark"
              :content="$t('EmailRows_Instance_Description_ToolTip')"
              placement="right"
            >
              <i class="el-icon-info" />
            </el-tooltip>
          </div>
          <div class="usage-summary__breakdown">
            <span
              v-for="(part, index) in usageBreakdown"
              :key="part.key"
              class="usage-summary__part"
            >
              {{ part.text }}
              <span
                v-if="index < usageBreakdown.length - 1"
                class="usage-summary__separator"
                aria-hidden="true"
                >•</span
              >
            </span>
          </div>
        </div>

        <div class="preview-card">
          <iframe :srcdoc="model.content" class="preview-iframe" />
        </div>

        <el-tabs v-model="activeUsageTab" class="usage-tabs">
          <el-tab-pane
            :label="$t('EmailRows_Table_Title_CampaignsAndTemplates')"
            name="campaigns"
          >
            <synced-row-campaigns-usage-table
              ref="tableCampaigns"
              :content-id="model.id"
            />
          </el-tab-pane>

          <el-tab-pane
            :label="$t('EmailRows_Table_Title_WorkflowCampaignsAndTemplates')"
            name="workflowCampaigns"
          >
            <synced-row-workflow-campaigns-usage-table
              ref="tableWorkflowCampaigns"
              :content-id="model.id"
            />
          </el-tab-pane>

          <el-tab-pane
            :label="$t('EmailRows_Table_Title_MessageTemplates')"
            name="messageTemplates"
          >
            <synced-row-message-template-usage-table
              ref="tableMessageTemplate"
              :content-id="model.id"
            />
          </el-tab-pane>
        </el-tabs>
      </div>
    </page-content>
  </page>
</template>

<script>
import Page from '@/common/components/page/Page'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import SyncedRowCampaignsUsageTable from '@/modules/email-saved-rows/components/SyncedRowCampaignsUsageTable'
import SyncedRowMessageTemplateUsageTable from '@/modules/email-saved-rows/components/SyncedRowMessageTemplateUsageTable'
import SyncedRowWorkflowCampaignsUsageTable from '@/modules/email-saved-rows/components/SyncedRowWorkflowCampaignsUsageTable'
import EditSingleRowMode from '@/common/components/email/EditSingleRowMode'
import {
  projectEndpoint,
  projectRoute,
  projectTimezoneDate
} from '@/core/helpers'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'EmailSyncedRowDetailsPage',
  components: {
    PageContent,
    PageHeaderNew,
    Page,
    SyncedRowCampaignsUsageTable,
    SyncedRowMessageTemplateUsageTable,
    SyncedRowWorkflowCampaignsUsageTable,
    EditSingleRowMode
  },

  data() {
    return {
      model: null,
      isLoading: false,
      isEditing: false,
      syncPollInterval: null,
      lastStaleCount: null,
      activeUsageTab: 'campaigns',
      campaignCount: 0,
      campaignMessageCount: 0,
      messageTemplateCount: 0,
      campaignTemplateCount: 0
    }
  },

  computed: {
    breadcrumbs() {
      return [
        {
          label: this.$t('EmailRows_Tab_Synced'),
          route: projectRoute({ name: 'EmailRowsSynced' })
        },
        {
          label: this.model ? this.model.title || this.model.name : ''
        }
      ]
    },

    usageCount() {
      return this.campaignMessageCount
    },

    usageBreakdown() {
      return [
        {
          key: 'campaigns',
          text: this.$tc('EmailRows_Count_Campaigns', this.campaignCount, {
            count: this.campaignCount
          })
        },
        {
          key: 'campaignTemplates',
          text: this.$tc(
            'EmailRows_Count_CampaignTemplates',
            this.campaignTemplateCount,
            { count: this.campaignTemplateCount }
          )
        },
        {
          key: 'messageTemplates',
          text: this.$tc(
            'EmailRows_Count_MessageTemplates',
            this.messageTemplateCount,
            { count: this.messageTemplateCount }
          )
        }
      ]
    }
  },

  mounted() {
    this.fetchData()
  },

  beforeDestroy() {
    this.stopSyncPolling()
  },

  methods: {
    formatDate(timestamp) {
      return projectTimezoneDate(timestamp).format('DD/MM/YYYY HH:mm')
    },

    async fetchData() {
      const id = this.$route.query.id
      if (!id) return

      this.isLoading = true
      try {
        const rowEndpoint = projectEndpoint({
          method: 'get',
          url: `email-saved-rows/${id}`,
          params: {
            expand: 'creator,updater'
          }
        })
        const rowResponse = await RestApi(rowEndpoint)
        this.model = rowResponse.data

        const countsEndpoint = projectEndpoint({
          method: 'get',
          url: `content/${id}/usages`
        })
        const countsResponse = await RestApi(countsEndpoint)
        const counts = countsResponse.data
        this.campaignCount = counts.campaign_count || 0
        this.campaignMessageCount = counts.campaign_message_count || 0
        this.messageTemplateCount = counts.message_template_count || 0
        this.campaignTemplateCount = counts.campaign_template_count || 0
      } catch (error) {
        this.$message.error(this.$t('EmailRows_FetchError'))
      } finally {
        this.isLoading = false
      }
    },

    openEditor() {
      this.isEditing = true
    },

    handleEditorSave() {
      this.isEditing = false
      this.$message.success(this.$t('EmailRows_EditSuccess'))
      this.refreshTables()
      this.fetchData()
      this.startSyncPolling()
    },

    refreshTables() {
      const tables = [
        'tableCampaigns',
        'tableWorkflowCampaigns',
        'tableMessageTemplate'
      ]
      tables.forEach(ref => {
        if (this.$refs[ref]) {
          this.$refs[ref].refresh()
        }
      })
    },

    startSyncPolling() {
      this.stopSyncPolling()
      this.lastStaleCount = null
      this.schedulePoll(0)
    },

    schedulePoll(count) {
      const delay = count < 5 ? 1000 : 4000
      this.syncPollInterval = setTimeout(
        () => this.pollSyncStatus(count),
        delay
      )
    },

    stopSyncPolling() {
      if (this.syncPollInterval) {
        clearTimeout(this.syncPollInterval)
        this.syncPollInterval = null
      }
    },

    async pollSyncStatus(count) {
      if (!this.model) return
      try {
        const endpoint = projectEndpoint({
          method: 'get',
          url: `content/${this.model.id}/sync-status`
        })
        const response = await RestApi(endpoint)
        const { in_sync: inSync, stale_count: staleCount } = response.data

        if (staleCount !== this.lastStaleCount) {
          this.lastStaleCount = staleCount
          this.refreshTables()
        }

        if (!inSync) {
          this.schedulePoll(count + 1)
        }
      } catch (e) {
        this.stopSyncPolling()
      }
    },

    handleEditorCancel() {
      this.isEditing = false
    },

    backToList() {
      this.$router.push(projectRoute({ name: 'EmailRowsSynced' }))
    },

    confirmDelete() {
      this.$confirm(
        this.$t('EmailRows_DeleteConfirmMessage', {
          name: this.model.title || this.model.name
        }),
        this.$t('EmailRows_DeleteConfirmTitle'),
        {
          confirmButtonText: this.$t('EmailRows_Action_Delete'),
          cancelButtonText: this.$t('EmailRows_Action_Cancel'),
          dangerouslyUseHTMLString: true,
          customClass: 'confirm-delete-dialog'
        }
      )
        .then(() => {
          this.deleteRow()
        })
        .catch(() => {})
    },

    async deleteRow() {
      try {
        const endpoint = projectEndpoint({
          method: 'delete',
          url: `email-saved-rows/${this.model.id}`
        })
        await RestApi(endpoint)
        this.$message.success(this.$t('EmailRows_DeleteSuccess'))
        this.backToList()
      } catch (error) {
        this.$message.error(this.$t('EmailRows_DeleteError'))
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.details-container {
  padding: 0 20px;
}

.meta-info {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: stretch;
  border-bottom: 1px solid #ebeef5;
  margin-bottom: 24px;
  padding-bottom: 16px;

  &__item {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 4px 24px;
    color: #4b515f;
    font-size: 14px;

    & + & {
      border-left: 1px solid #ebeef5;
    }

    &:first-child {
      padding-left: 0;
    }

    .label {
      font-weight: 600;
    }
    .username {
      font-weight: 400;
    }
  }

  &__value {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__avatar-fallback {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #ebeef5;
    color: #909399;
    font-size: 12px;
  }

  .updated-at {
    color: #909399;
  }
}

.preview-card {
  background: #ffffff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  margin-bottom: 32px;
  height: 308px;
  padding: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-iframe {
  width: 99%;
  height: 99%;
  border: none;
}

.usage-summary {
  margin-bottom: 24px;
  color: #4b515f;
  font-size: 14px;

  &__total {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin-bottom: 4px;
  }

  &__label {
    font-weight: 600;
  }

  &__total-count {
    font-weight: 400;
  }

  &__breakdown {
    display: flex;
    flex-wrap: wrap;
    color: #4b515f;
  }

  &__part {
    display: inline-flex;
    align-items: center;
  }

  &__separator {
    margin: 0 8px;
    color: #c0c4cc;
  }
}

.usage-tabs {
  margin-top: 8px;

  ::v-deep .el-tabs__item {
    color: #4b515f;
    font-size: 14px;
    font-weight: 600;
  }
}

.btn-underlined {
  font-weight: 400;
  font-size: 14px;
  line-height: 24px;
  text-decoration: underline;
  color: #1d87c9;
  cursor: pointer;
  margin-right: 20px;

  &.disabled {
    color: #dbdee7;
    cursor: not-allowed;
  }
}
</style>

<style lang="scss">
.edit-synced-row-dialog {
  display: flex;
  flex-direction: column;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .edit-single-row-modal {
    width: 100% !important;
    max-width: 100% !important;
    height: 100% !important;
    border-radius: 0 !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/modules/email-saved-rows/pages/EmailSyncedRowDetailsPage.vue